import { createContext, useCallback, useContext } from 'react';
import { css } from '@emotion/react';
import { Link } from '@tanstack/react-location';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { Bool } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { ClientSettingsApi } from '@frontend/api-client-settings';
import { FaxQueries } from '@frontend/api-fax';
import { FormsSubmission } from '@frontend/api-forms';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { ContactsHooks } from '@frontend/contacts';
import { Dashboard } from '@frontend/dashboard';
import { useFeatureAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { Icon, type IconName } from '@frontend/icons';
import { useLocationDataShallowStore } from '@frontend/location-helpers';
import { MultiSettingsHooks } from '@frontend/multi-settings';
import { usePaymentsModalButton, PaymentRequestModal } from '@frontend/payments-collection-flow';
import { useGetScheduleRequestAndAlertsCount } from '@frontend/schedule';
import { useScopedAppFlagStore, useAppScopeStore } from '@frontend/scope';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { useTeamChatStore } from '@frontend/team-chat';
import { useTeamChatSelector } from '@frontend/team-chat-new';
import { theme } from '@frontend/theme';
import { Text, NotificationBadge, NakedButton } from '@frontend/design-system';

interface AllAppsModule {
  showHeader?: boolean;
  handleModalClose?: () => void;
}

const AllAppsModuleContext = createContext<AllAppsModule['handleModalClose'] | undefined>(undefined);

export const useAllAppsModule = () => useContext(AllAppsModuleContext);

export const AllAppsModule = ({ showHeader = true, handleModalClose }: AllAppsModule) => {
  const { t } = useTranslation('dashboard');
  const hasListHomeIconAccess = ContactsHooks.useHasListHomeIconAccess();

  return (
    <AllAppsModuleContext.Provider value={handleModalClose}>
      {showHeader && <Dashboard.Module.Header title={t('All Apps')} />}
      <Dashboard.Module.Content>
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            row-gap: ${theme.spacing(2.5)};
            padding: ${theme.spacing(1)};
            margin-top: ${theme.spacing(3)};
          `}
        >
          <Home />
          <PhoneAppFeatureButton />
          <VoicemailAppFeatureButton />
          <MessagesAppFeatureButton />
          <AppFeatureButton icon='contacts' to='/contacts/all-contacts' label={t('Contacts')} />
          <AppFeatureButton icon='lists' to='/contacts/lists' label={t('Lists')} show={hasListHomeIconAccess} />
          <CalendarAppFeatureButton />
          <TeamChatFeatureButton />
          <FormsAppFeatureButton />
          <AppFeatureButton icon='analytics' to='/analytics/dashboard' label={t('Analytics')} />
          <AppFeatureButton icon='reviews-dash' to='/reviews/all-reviews' label={t('Reviews')} />
          <PaymentsAppFeatureButton />
          <FaxAppFeatureButton />
        </div>
      </Dashboard.Module.Content>
    </AllAppsModuleContext.Provider>
  );
};

const PaymentsAppFeatureButton = () => {
  const { paymentRequestModalProps, triggerProps, hidePaymentRequestModal } = usePaymentsModalButton();
  const { t } = useTranslation('dashboard');

  return hidePaymentRequestModal ? (
    <AppFeatureButton icon='payments' to='/payments/invoices' label={t('Payments')} />
  ) : (
    <>
      <AppFeatureButton icon='payments' onClick={triggerProps.onClick} label={t('Payments')} />
      <PaymentRequestModal {...paymentRequestModalProps} />
    </>
  );
};

const CalendarAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const { scheduleAlertsCount } = useGetScheduleRequestAndAlertsCount();

  const handleOpenSchedulePulse = useCallback(() => {
    dispatchTopBarTraySelectionEvent('schedulePulse');
  }, []);

  return (
    <AppFeatureButton
      count={scheduleAlertsCount}
      icon='calendar'
      onClick={handleOpenSchedulePulse}
      label={t('Schedule')}
    />
  );
};

const PhoneAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');

  return <AppFeatureButton icon='phone' to='/calls/call-history' label={t('Phone')} />;
};

const TeamChatV2 = () => {
  const { t } = useTranslation('dashboard');

  const { totalUnreadCount, isTrayOpen, setTrayOpen, setIsChatListExpanded } = useTeamChatStore([
    'totalUnreadCount',
    'isTrayOpen',
    'unreadMessageCount',
    'setTrayOpen',
    'setIsChatListExpanded',
  ]);

  return (
    <AppFeatureButton
      icon='chat'
      count={totalUnreadCount}
      onClick={() => {
        setTrayOpen(!isTrayOpen);
        if (!isTrayOpen) {
          setIsChatListExpanded(false);
        }
      }}
      label={t('Team Chat')}
    />
  );
};

const TeamChatV2RemasteredButton = () => {
  const { t } = useTranslation('dashboard');
  const unreadMentionsCount = useTeamChatSelector((ctx) => ctx?.unreadMentionsCount);
  const unreadMessagesCount = useTeamChatSelector((ctx) => ctx?.unreadMessagesCount);
  const openTeamChat = useTeamChatSelector((ctx) => ctx?.openTeamChat);

  return (
    <AppFeatureButton
      icon='chat'
      count={(unreadMentionsCount ?? 0) + unreadMessagesCount}
      onClick={() => {
        openTeamChat();
      }}
      label={t('Team Chat')}
    />
  );
};

const VoicemailAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const voicemailsUnreadCount = PhoneCallsQueries.useVoicemailsUnreadCount();

  return (
    <AppFeatureButton icon='voicemail' to='/calls/voicemails' label={t('Voicemails')} count={voicemailsUnreadCount} />
  );
};

const Home = () => {
  const { t } = useTranslation('dashboard');

  return <AppFeatureButton icon='home' to='/home/dashboard' label={t('Home')} />;
};

const MessagesAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const { locationId } = useLocationDataShallowStore('locationId');

  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();

  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const { aggregatedValue: autoMessageInboxVisibility, isLoading: isAutoMessageInboxSettingsLoading } =
    MultiSettingsHooks.useBooleanClientSetting({
      set: ClientSettingsApi.querySetKeys.sets.autoMessageInboxVisibility,
      key: ClientSettingsApi.querySetKeys.keys.autoMessageInboxVisibility,
      defaultValue: 'false',
      locationIds: selectedLocationIds,
    });
  const avoidLastAutomated = autoMessageInboxVisibility !== true;

  const actionableTabVisibilityFF = getFeatureFlagValue('nwx:inbox-actionable-tab');
  const { data: actionableTabIsEnabled, isLoading: isEnableActionableTabLoading } =
    MessagingSettingsV1.Queries.useListQuery(
      {
        settingType: SettingType_Enum.INBOX_LOCATION_SETTING,
        orgId: selectedOrgId,
        locationIds: selectedLocationIds,
      },
      {
        enabled: !!actionableTabVisibilityFF,
        select: (data) => {
          return data.settings.some(({ setting }) => {
            if ('inboxLocationSetting' in setting) {
              return setting.inboxLocationSetting.enableActionableTab ?? false;
            }
            return false;
          });
        },
      }
    );

  const isActionableFeatureEnabled = actionableTabIsEnabled && actionableTabVisibilityFF;

  const { data: inboxUnreadCount } = SMSDataV3.Queries.useListThreadsCountQuery({
    httpOptions: {
      onFetchError: (error) => {
        if (error.message.includes('too many')) {
          console.warn(error);
        } else {
          throw error;
        }
      },
    },
    request: {
      locationId,
      groupIds: selectedLocationIds,
      ...(!isActionableFeatureEnabled && { avoidLastAutomated, statuses: [SMSDataV3.Types.KnownThreadStatus.NEW] }),
      actionable: isActionableFeatureEnabled ? Bool.BOOL_TRUE : Bool.BOOL_UNSPECIFIED,
    },
    options: {
      select: (data) => data.count,
      enabled: !!selectedLocationIds[0] && !isAutoMessageInboxSettingsLoading && !isEnableActionableTabLoading,
    },
  });

  return <AppFeatureButton count={inboxUnreadCount} icon='messaging' to='/messages/inbox' label={t('Messages')} />;
};

const FaxAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const { selectedLocationIds } = useAppScopeStore();
  const { hasAccess: hasFaxAccess } = useFeatureAccess('fax-2-0', Permission.FAX_INBOX_MANAGE);

  const { data: faxUnreadCount } = FaxQueries.useCountFaxes(selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0,
  });

  if (!hasFaxAccess) return null;

  return <AppFeatureButton count={faxUnreadCount?.faxCount} icon='fax' to='/fax/inbox' label={t('Fax')} />;
};

const TeamChatFeatureButton = () => {
  const user = getUser();
  const weaveUser = isWeaveUser();
  const { getCustomizationFlagValue, getFeatureFlagValue } = useScopedAppFlagStore();
  const hasChatCustomizationFlag = getCustomizationFlagValue('chat');
  const isTeamChatV2Remastered = getFeatureFlagValue('team-chat-2-remastered');

  const shouldHaveChat = hasChatCustomizationFlag && user && !weaveUser;

  if (!shouldHaveChat) return null;

  return isTeamChatV2Remastered ? <TeamChatV2RemasteredButton /> : <TeamChatV2 />;
};

const FormsAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const formsSubmissionCount = FormsSubmission.useSubmissionsUnreadCount();

  return (
    <AppFeatureButton
      count={formsSubmissionCount.data?.count}
      icon='forms'
      to='/forms/submissions'
      label={t('Forms')}
    />
  );
};

type AppFeatureButtonProps = {
  icon: IconName;
  label: string;
  to?: string;
  show?: boolean;
  size?: 'large' | 'small';
  count?: number;
  onClick?: () => void;
};

export const AppFeatureButton = ({ icon, label, to, show, size = 'large', count, onClick }: AppFeatureButtonProps) => {
  const handleModalClose = useAllAppsModule();

  const handleClick = useCallback(() => {
    onClick?.();
    handleModalClose?.();
  }, [handleModalClose, onClick]);

  if (show === false) return null;

  const Component = onClick ? NakedButton : Link;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(1)};
        align-items: center;
        position: relative;
      `}
    >
      <Component
        to={to}
        onClick={handleClick}
        css={css`
          width: ${size === 'large' ? '72px' : '60px'};
          height: ${size === 'large' ? '72px' : '60px'};
          background-color: ${theme.colors.primary50};
          color: ${theme.colors.white};
          border-radius: ${theme.borderRadius.full};
          display: flex;
          justify-content: center;
          align-items: center;
          transition: 0.3s ease;
          position: relative;
          :hover,
          :focus {
            box-shadow: ${theme.shadows.floating};
          }
          &:hover,
          &:active {
            transform: scale(1.05);
            box-shadow: ${theme.shadows.heavy};
          }

          &:active {
            transform: scale(0.95);
          }
        `}
      >
        <Icon name={icon} size={32} />
        {!!count && (
          <NotificationBadge truncateCount css={{ position: 'absolute', right: 0, top: 0 }}>
            {count}
          </NotificationBadge>
        )}
      </Component>
      <Text size='small'>{label}</Text>
    </div>
  );
};
